import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePermission } from '@/hooks/usePermission';
import { mockPurchaseRequestService } from '@/services/mock/MockPurchaseRequestService';
import {
  PurchaseRequest,
  ProcurementCategory,
  PriorityLevel,
  ExpenseType,
  BudgetSummary,
  DocumentAttachment,
} from '@/types/domain';
import { ROUTES } from '@/constants/routes';
import { RequestHeaderSection } from './RequestHeaderSection';
import { ItemManagementSection, MaterialItemState } from './ItemManagementSection';
import { ServiceDetailsSection } from './ServiceDetailsSection';
import { BusinessJustificationSection } from './BusinessJustificationSection';
import { RequestReviewModal } from './RequestReviewModal';
import { SubmissionActions } from './SubmissionActions';
import { BudgetSummaryWidget } from '@/components/data-display/BudgetSummaryWidget';
import { DocumentUploader } from '@/components/forms/DocumentUploader';
import { Button } from '@/components/ui/Button';
import { AlertCircle, ArrowLeft } from 'lucide-react';

export interface PurchaseRequestFormProps {
  mode?: 'CREATE' | 'EDIT_DRAFT' | 'DUPLICATE';
  initialData?: PurchaseRequest | null;
  defaultProcurementType?: 'Material' | 'Service';
}

export const PurchaseRequestForm: React.FC<PurchaseRequestFormProps> = ({
  mode = 'CREATE',
  initialData = null,
  defaultProcurementType = 'Material',
}) => {
  const navigate = useNavigate();
  const { user } = usePermission();

  // Header State
  const [title, setTitle] = useState(initialData?.title || '');
  const [category, setCategory] = useState<ProcurementCategory>(
    initialData?.category || 'CapitalEquipment'
  );
  const [procurementType, setProcurementType] = useState<'Material' | 'Service'>(
    initialData?.procurementType || defaultProcurementType
  );
  const [expenseType, setExpenseType] = useState<ExpenseType>(initialData?.expenseType || 'OPEX');
  const [priority, setPriority] = useState<PriorityLevel>(initialData?.priority || 'Medium');
  const [requiredDate, setRequiredDate] = useState(
    initialData?.requiredDate || new Date(Date.now() + 14 * 86400000).toISOString().split('T')[0]
  );
  const [companyCode, setCompanyCode] = useState(initialData?.companyCode || '1000');
  const [plantCode, setPlantCode] = useState(initialData?.plantCode || user?.plantCode || 'PLANT-1000');
  const [departmentCode, setDepartmentCode] = useState(
    initialData?.departmentCode || user?.departmentCode || 'DEP-IT'
  );
  const [costCenterCode, setCostCenterCode] = useState(
    initialData?.costCenterCode || 'CC-IT-101'
  );
  const [justification, setJustification] = useState(initialData?.justification || '');

  // Material Line Items State
  const [items, setItems] = useState<MaterialItemState[]>(
    initialData?.items?.map((i): MaterialItemState => ({
      id: i.id,
      isCatalog: i.materialCode !== 'NON-CATALOG',
      materialCode: i.materialCode,
      materialName: i.materialName,
      category: i.category,
      quantity: i.quantity,
      unitOfMeasure: i.unitOfMeasure,
      estimatedUnitPrice: i.estimatedUnitPrice,
      requiredDate: i.requiredDate || '',
      specifications: i.specifications || '',
      costCenterCode: i.costCenterCode || costCenterCode || 'CC-IT-101',
      plantCode: i.plantCode || plantCode || 'PLANT-1000',
      glAccountCode: i.glAccountCode,
      preferredVendor: i.preferredVendor,
      deliveryLocation: i.deliveryLocation,
    })) || [
      {
        id: 'item-init-1',
        isCatalog: true,
        materialCode: 'MAT-COMP-01',
        materialName: 'High-Power Compute Node X1',
        category: 'CapitalEquipment',
        quantity: 2,
        unitOfMeasure: 'EA',
        estimatedUnitPrice: 4500,
        requiredDate: new Date(Date.now() + 14 * 86400000).toISOString().split('T')[0] || '',
        specifications: 'Dual GPU acceleration, 128GB ECC RAM',
        costCenterCode: 'CC-IT-101',
        plantCode: 'PLANT-1000',
        glAccountCode: '500100',
        deliveryLocation: 'Austin Tech Hub',
      },
    ]
  );

  // Service Details State
  const [serviceName, setServiceName] = useState(
    initialData?.serviceDetails?.serviceName || initialData?.title || ''
  );
  const [serviceCategory, setServiceCategory] = useState(
    initialData?.serviceDetails?.serviceCategory || 'ITServices'
  );
  const [scopeOfWork, setScopeOfWork] = useState(initialData?.serviceDetails?.scopeOfWork || '');
  const [startDate, setStartDate] = useState(
    initialData?.serviceDetails?.startDate || new Date().toISOString().split('T')[0]
  );
  const [endDate, setEndDate] = useState(
    initialData?.serviceDetails?.endDate ||
      new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0]
  );
  const [location, setLocation] = useState(
    initialData?.serviceDetails?.location || 'Austin Technology Campus'
  );
  const [estimatedServiceValue, setEstimatedServiceValue] = useState<number>(
    initialData?.totalAmount || 15000
  );
  const [suggestedSupplier, setSuggestedSupplier] = useState(
    initialData?.serviceDetails?.suggestedSupplier || ''
  );
  const [commercialTerms, setCommercialTerms] = useState(
    initialData?.serviceDetails?.commercialTerms || 'Net 30 Days upon milestone sign-off'
  );

  // Document Attachments State
  const [attachments, setAttachments] = useState<DocumentAttachment[]>([]);
  const [budgetSummary, setBudgetSummary] = useState<BudgetSummary | null>(null);

  // Form Controls State
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isReviewOpen, setIsReviewOpen] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);

  // Fetch Budget on Cost Center change
  useEffect(() => {
    mockPurchaseRequestService.getBudget(costCenterCode).then(setBudgetSummary).catch(console.error);
  }, [costCenterCode]);

  const handleUploadDoc = async (file: File, category: string) => {
    const uploaded = await mockPurchaseRequestService.uploadDocument(initialData?.id || 'TEMP', file, category);
    setAttachments((prev) => [...prev, uploaded]);
  };

  const handleDeleteDoc = async (docId: string) => {
    await mockPurchaseRequestService.deleteDocument(docId);
    setAttachments((prev) => prev.filter((d) => d.id !== docId));
  };

  // Calculate Requisition Total
  const totalAmount =
    procurementType === 'Material'
      ? items.reduce(
          (acc, i) => acc + (Number(i.quantity) || 0) * (Number(i.estimatedUnitPrice) || 0),
          0
        )
      : estimatedServiceValue;

  const validateForm = (): boolean => {
    const errs: Record<string, string> = {};
    if (!title.trim() || title.length < 5) {
      errs.title = 'Title must be at least 5 characters long';
    }
    if (!justification.trim() || justification.length < 15) {
      errs.justification = 'Justification must be at least 15 characters long';
    }
    if (!requiredDate) {
      errs.requiredDate = 'Required date is required';
    }

    if (procurementType === 'Material') {
      if (items.length === 0) {
        errs.items = 'At least one material line item is required';
      }
    } else {
      if (!serviceName.trim()) {
        errs.serviceName = 'Service name is required';
      }
      if (!scopeOfWork.trim() || scopeOfWork.length < 20) {
        errs.scopeOfWork = 'Detailed Scope of Work must be at least 20 characters';
      }
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSaveDraft = async () => {
    try {
      setIsSavingDraft(true);
      const draftPayload = {
        title: title || 'Untitled Requisition Draft',
        category,
        procurementType,
        expenseType,
        priority,
        companyCode,
        plantCode: plantCode || 'PLANT-1000',
        departmentCode: departmentCode || 'DEP-IT',
        costCenterCode: costCenterCode || 'CC-IT-101',
        justification,
        requiredDate: requiredDate || '',
        items:
          procurementType === 'Material'
            ? items.map((i, idx) => ({
                id: i.id,
                requestId: 'DRAFT',
                itemNumber: (idx + 1) * 10,
                materialCode: i.isCatalog ? i.materialCode : 'NON-CATALOG',
                materialName: i.materialName,
                category: i.category,
                quantity: i.quantity,
                unitOfMeasure: i.unitOfMeasure,
                estimatedUnitPrice: i.estimatedUnitPrice,
                totalEstimatedPrice: i.quantity * i.estimatedUnitPrice,
                currency: 'INR',
                requiredDate: i.requiredDate || requiredDate || '',
                specifications: i.specifications,
                costCenterCode: i.costCenterCode || costCenterCode || 'CC-IT-101',
                plantCode: i.plantCode || plantCode || 'PLANT-1000',
              }))
            : [],
        serviceDetails:
          procurementType === 'Service'
            ? {
                serviceName: serviceName || title,
                serviceCategory,
                scopeOfWork,
                startDate: startDate || '',
                endDate: endDate || '',
                location,
                suggestedSupplier,
                commercialTerms,
              }
            : undefined,
      };

      const saved = await mockPurchaseRequestService.saveDraft(draftPayload);
      setIsDirty(false);
      setLastSavedAt(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
      if (mode === 'CREATE') {
        navigate(ROUTES.REQUESTS.DETAILS(saved.id));
      }
    } catch {
      alert('Failed to save draft. Please retry.');
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleOpenReview = () => {
    if (validateForm()) {
      setIsReviewOpen(true);
    }
  };

  const handleFinalSubmit = async () => {
    try {
      setIsSubmitting(true);
      const idempotencyKey = `submit-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`;
      const created = await mockPurchaseRequestService.createRequest({
        title,
        category,
        procurementType,
        expenseType,
        priority,
        companyCode,
        plantCode: plantCode || 'PLANT-1000',
        departmentCode: departmentCode || 'DEP-IT',
        costCenterCode: costCenterCode || 'CC-IT-101',
        justification,
        requiredDate: requiredDate || '',
        departmentId: user?.departmentCode || 'DEP-IT',
        plantId: plantCode || 'PLANT-1000',
        costCenterId: costCenterCode || 'CC-IT-101',
        departmentName: user?.departmentName || 'Information Technology',
        requesterId: user?.id || 'usr-101',
        requesterName: user?.name || 'Sarah Jenkins',
        idempotencyKey,
        items:
          procurementType === 'Material'
            ? items.map((i, idx) => ({
                id: i.id,
                requestId: 'TEMP',
                itemNumber: (idx + 1) * 10,
                materialCode: i.isCatalog ? i.materialCode : 'NON-CATALOG',
                materialName: i.materialName,
                category: i.category,
                quantity: i.quantity,
                unitOfMeasure: i.unitOfMeasure,
                estimatedUnitPrice: i.estimatedUnitPrice,
                totalEstimatedPrice: i.quantity * i.estimatedUnitPrice,
                currency: 'INR',
                requiredDate: i.requiredDate || requiredDate || '',
                specifications: i.specifications,
                costCenterCode: i.costCenterCode || costCenterCode || 'CC-IT-101',
                plantCode: i.plantCode || plantCode || 'PLANT-1000',
              }))
            : [
                {
                  id: `item-srv-${Date.now()}`,
                  requestId: 'TEMP',
                  itemNumber: 10,
                  materialCode: 'NON-CATALOG-SERVICE',
                  materialName: serviceName || title,
                  category: 'ITServices',
                  quantity: 1,
                  unitOfMeasure: 'LOT',
                  estimatedUnitPrice: estimatedServiceValue,
                  totalEstimatedPrice: estimatedServiceValue,
                  currency: 'INR',
                  requiredDate: startDate || '',
                  specifications: scopeOfWork,
                  costCenterCode: costCenterCode || 'CC-IT-101',
                  plantCode: plantCode || 'PLANT-1000',
                },
              ],
        serviceDetails:
          procurementType === 'Service'
            ? {
                serviceName: serviceName || title,
                serviceCategory,
                scopeOfWork,
                startDate: startDate || '',
                endDate: endDate || '',
                location,
                suggestedSupplier,
                commercialTerms,
              }
            : undefined,
      });

      navigate(ROUTES.REQUESTS.DETAILS(created.id));
    } catch {
      alert('Failed to submit requisition. Please retry.');
    } finally {
      setIsSubmitting(false);
      setIsReviewOpen(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6 font-poppins">
      {/* Header Banner */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border pb-4">
        <div>
          <div className="flex items-center gap-2.5">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(ROUTES.REQUESTS.LIST)}
              className="p-1.5 h-8 w-8 rounded-xl hover:bg-muted cursor-pointer text-foreground-secondary hover:text-foreground shrink-0"
              title="Back to Requisitions Table"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-2xl font-bold tracking-tight text-foreground font-poppins">
              {mode === 'EDIT_DRAFT'
                ? 'Edit Purchase Requisition Draft'
                : mode === 'DUPLICATE'
                ? 'Duplicate Purchase Requisition'
                : 'Create Purchase Requisition'}
            </h1>
          </div>
          <p className="text-xs text-foreground-secondary mt-0.5 pl-10.5">
            {mode === 'DUPLICATE'
              ? `Creating a new requisition initialized from existing template (Provenance tracked).`
              : `Internal procurement transaction module for material and service requirements.`}
          </p>
        </div>
      </div>

      {/* Global Validation Errors Banner */}
      {Object.keys(errors).length > 0 && (
        <div className="p-3.5 rounded-xl border border-destructive/40 bg-destructive/10 text-destructive text-xs flex items-center gap-2 font-medium">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>Please resolve highlighted validation errors before proceeding.</span>
        </div>
      )}

      {/* Section 1: Header Context */}
      <RequestHeaderSection
        title={title}
        setTitle={(v) => {
          setTitle(v);
          setIsDirty(true);
        }}
        category={category}
        setCategory={(v) => {
          setCategory(v);
          setIsDirty(true);
        }}
        procurementType={procurementType}
        setProcurementType={(v) => {
          setProcurementType(v);
          setIsDirty(true);
        }}
        expenseType={expenseType}
        setExpenseType={(v) => {
          setExpenseType(v);
          setIsDirty(true);
        }}
        priority={priority}
        setPriority={(v) => {
          setPriority(v);
          setIsDirty(true);
        }}
        requiredDate={requiredDate || ''}
        setRequiredDate={(v) => {
          setRequiredDate(v);
          setIsDirty(true);
        }}
        companyCode={companyCode}
        setCompanyCode={setCompanyCode}
        plantCode={plantCode}
        setPlantCode={setPlantCode}
        departmentCode={departmentCode}
        setDepartmentCode={setDepartmentCode}
        costCenterCode={costCenterCode}
        setCostCenterCode={setCostCenterCode}
        requesterName={user?.name}
        employeeId={user?.employeeId}
        errors={errors}
      />

      {/* Section 2: Items or Service Scope */}
      {procurementType === 'Material' ? (
        <ItemManagementSection
          items={items}
          setItems={(newItems) => {
            setItems(newItems);
            setIsDirty(true);
          }}
          defaultCategory={category}
          defaultCostCenter={costCenterCode}
          defaultPlant={plantCode}
          defaultRequiredDate={requiredDate || ''}
        />
      ) : (
        <ServiceDetailsSection
          serviceName={serviceName}
          setServiceName={(v) => {
            setServiceName(v);
            setIsDirty(true);
          }}
          serviceCategory={serviceCategory}
          setServiceCategory={(v) => {
            setServiceCategory(v);
            setIsDirty(true);
          }}
          scopeOfWork={scopeOfWork}
          setScopeOfWork={(v) => {
            setScopeOfWork(v);
            setIsDirty(true);
          }}
          startDate={startDate || ''}
          setStartDate={(v) => {
            setStartDate(v);
            setIsDirty(true);
          }}
          endDate={endDate || ''}
          setEndDate={(v) => {
            setEndDate(v);
            setIsDirty(true);
          }}
          location={location}
          setLocation={(v) => {
            setLocation(v);
            setIsDirty(true);
          }}
          estimatedValue={estimatedServiceValue}
          setEstimatedValue={(v) => {
            setEstimatedServiceValue(v);
            setIsDirty(true);
          }}
          suggestedSupplier={suggestedSupplier}
          setSuggestedSupplier={(v) => {
            setSuggestedSupplier(v);
            setIsDirty(true);
          }}
          commercialTerms={commercialTerms}
          setCommercialTerms={(v) => {
            setCommercialTerms(v);
            setIsDirty(true);
          }}
          errors={errors}
        />
      )}

      {/* Section 3: Budget Impact Widget */}
      {budgetSummary && (
        <BudgetSummaryWidget budget={budgetSummary} requestAmount={totalAmount} />
      )}

      {/* Section 4: Supporting Documents */}
      <DocumentUploader documents={attachments} onUpload={handleUploadDoc} onDelete={handleDeleteDoc} />

      {/* Section 5: Business Justification */}
      <BusinessJustificationSection
        justification={justification}
        setJustification={(v) => {
          setJustification(v);
          setIsDirty(true);
        }}
        error={errors.justification}
      />

      {/* Sticky Action Toolbar */}
      <SubmissionActions
        onSaveDraft={handleSaveDraft}
        onSubmitReview={handleOpenReview}
        onCancel={() => navigate(ROUTES.REQUESTS.LIST)}
        isSavingDraft={isSavingDraft}
        isSubmitting={isSubmitting}
        isDirty={isDirty}
        lastSavedAt={lastSavedAt}
      />

      {/* Pre-Submission Confirmation Modal */}
      <RequestReviewModal
        isOpen={isReviewOpen}
        onClose={() => setIsReviewOpen(false)}
        onConfirmSubmit={handleFinalSubmit}
        isSubmitting={isSubmitting}
        title={title}
        category={category}
        procurementType={procurementType}
        expenseType={expenseType}
        priority={priority}
        requiredDate={requiredDate || ''}
        companyCode={companyCode}
        plantCode={plantCode}
        departmentCode={departmentCode}
        costCenterCode={costCenterCode}
        justification={justification}
        totalAmount={totalAmount}
        itemsCount={items.length}
        budgetSummary={budgetSummary}
        attachments={attachments}
      />
    </div>
  );
};
